/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSelector } from '@reduxjs/toolkit';
import { initialState } from 'modules/dashboard/slice';
import { SLICE } from 'modules/dashboard/constants';
//
const selectDomain = (state) => state[SLICE.DASHBOARD] || initialState;
/**
 * Getting loader state to dashboard component
 */
export const selectLoader = createSelector([selectDomain], (state) => state.loading);
/**
 * Getting is session timed out state for dashboard component
 */
export const selectIsSessionTimedOut = createSelector(
  [selectDomain],
  (state) => state.isSessionTimedOut
);
/**
 * Getting embed token state to dashboard component
 */
export const selectEmbedToken = createSelector([selectDomain], (state) => state.embedTokenDetails);
/**
 * Getting selected hotel name state to dashboard component
 */
export const selectHotelName = createSelector([selectDomain], (state) => state.hotelName);
/**
 * Getting visuals state to dashboard component
 */
export const selectVisuals = createSelector([selectDomain], (state) => state.visuals?.results);
/**
 * Getting visual filters state to dashboard component
 */
export const selectVisualFilters = createSelector(
  [selectDomain],
  (state) => state.visualFilters?.results
);
/**
 * Getting selected reportType state for dashboard component
 */
export const selectReportType = createSelector([selectDomain], (state) => state?.reportType);
/**
 *  Getting embed token expiry state to dashboard component
 */
export const selectEmbedTokenExpiry = createSelector(
  [selectDomain],
  (state) => state.embedTokenDetails?.expiry
);
/**
 * Getting dynamic utm breakdown state for dashboard component
 */
export const selectUtmBreakdownDetails = createSelector(
  [selectDomain],
  (state) => state.utmBreakdown
);
/**
 *  Getting hotel list state to dashboard component
 */
export const selectHotelDropdownList = createSelector([selectDomain], (state) => state.hotelList);
/**
 *  Getting hotel list state to dashboard component
 */
export const selectHotelsList = createSelector([selectDomain], (state) => state.hotelList?.results);
/**
 *  Getting hotel details by hotel id state to dashboard component
 */
const getFirstParam = (_, param) => param;
export const selectHotelById = createSelector(
  [selectHotelsList, getFirstParam],
  (hotelList, hotelId) => hotelList?.filter((hotel) => hotel?.id === hotelId)[0]
);
/**
 *  Getting latest updated hotel list state to dashboard component
 */
export const selectLatestUpdatedHotelsList = createSelector(
  [selectDomain],
  (state) => state.latestUpdatedHotelList?.results
);
/**
 *  Getting pms sync date of the hotel state to dashboard component
 */
export const selectPmsDate = createSelector([selectDomain], (state) => state.pmsDate);
/**
 *  Getting pms sync date of the hotel state to dashboard component
 */
export const selectGroupPmsDates = createSelector([selectDomain], (state) => state.groupPmsDates);
/**
 *  Getting pms sync state to dashboard component
 */
export const selectPmsSync = createSelector([selectDomain], (state) => state.pmsSync);

/**
 * Getting is signed in state for dashboard component
 */
export const selectIsSignedIn = createSelector([selectDomain], (state) => state?.isSignedIn);
/**
 *Getting data last updated date for dashboard component
 */
export const selectDataLastUpdated = createSelector(
  [selectDomain],
  (state) => state?.lastUpdatedDate
);
/**
 *  Getting hotel changed state
 */
export const selectHotelChangeState = createSelector([selectDomain], (state) => state.hotelChange);
/**
 *  Getting tab changed state
 */
export const selectTabChangeState = createSelector([selectDomain], (state) => state.tabChange);
/**
 *  Getting is demo user state to the component
 */
export const selectIsDemoUser = createSelector([selectDomain], (state) => state.isDemoUser);
/**
 *  Getting logged in user role state to the component
 */
export const selectUserRole = createSelector([selectDomain], (state) => state.userRole);
/**
 *  Getting tab access state to the component
 */
export const selectTabAccess = createSelector([selectDomain], (state) => state.tabAccess);
/**
 *  Getting is user configuration not found state to the component
 */
export const selectIsUserConfigurationNotExist = createSelector(
  [selectDomain],
  (state) => state.isUserConfigurationNotExist
);
/**
 * Getting is token revoked state for dashboard component
 */
export const selectTokenRevocation = createSelector(
  [selectDomain],
  (state) => state.isTokenRevoked
);
/**
 *  Getting tab value
 */
export const selectTabValue = createSelector([selectDomain], (state) => state.tabValue);
/**
 *  Getting generated current url
 */
export const selectCurrentUrl = createSelector([selectDomain], (state) => state.currentUrl);
/**
 *  Getting saved command filters state to dashboard component
 */
export const selectSavedCommandFilters = createSelector(
  [selectDomain],
  (state) => state.commandUrlFilterList
);
/**
 *  Getting saved command filter drop down list state to dashboard component
 */
export const selectCommandFilterDropdownList = createSelector(
  [selectDomain],
  (state) => state.commandUrlFilterDropdownList
);
/**
 *  Getting saved command filter loader state to dashboard component
 */
export const selectCommandFilterLoader = createSelector(
  [selectDomain],
  (state) => state.commandFilterLoader
);
/**
 * Getting dynamic breakdown state for dashboard component
 */
export const selectBreakdownDetails = createSelector([selectDomain], (state) => state.breakdown);
/**
 * Getting dynamic forecast filter state for dashboard component
 */
export const selectForecastFilter = createSelector(
  [selectDomain],
  (state) => state.forecastFilters
);
/**
 * Getting target date state to dashboard component
 */
export const selectTargetDate = createSelector([selectDomain], (state) => state.targetDate);
/**
 *  Getting pace horizon state to dashboard component
 */
export const selectPaceHorizon = createSelector(
  [selectDomain],
  (state) => state.summaryPaceHorizon
);
/**
 *  Getting pace variable state to dashboard component
 */
export const selectPaceVariable = createSelector(
  [selectDomain],
  (state) => state.summaryPaceVariable
);
/**
 *  Getting pace booking date state to dashboard component
 */
export const selectPaceBookingDate = createSelector(
  [selectDomain],
  (state) => state.paceBookingDate
);
/**
 *  Getting pace booking style state to dashboard component
 */
export const selectPaceBookingStyle = createSelector(
  [selectDomain],
  (state) => state.paceBookingStyle
);
/**
 *  Getting pace OTB state to dashboard component
 */
export const selectPaceOtb = createSelector([selectDomain], (state) => state.paceOtb);

/**
 *  Getting trend target date state to dashboard component
 */
export const selectTrendTargetDate = createSelector(
  [selectDomain],
  (state) => state.trendTargetDate
);
/**
 *  Getting trend booking date state to dashboard component
 */
export const selectTrendBookingDate = createSelector(
  [selectDomain],
  (state) => state.trendBookingDate
);
/**
 *  Getting trend OTB state to dashboard component
 */
export const selectTrendOtb = createSelector([selectDomain], (state) => state.trendOtb);
/**
 *  Getting trend booking style state to dashboard component
 */
export const selectTrendBookingStyle = createSelector(
  [selectDomain],
  (state) => state.trendBookingStyle
);
/**
 *  Getting weekly trend tabs start target date state to dashboard component
 */
export const selectWeeklyTrendStartDate = createSelector(
  [selectDomain],
  (state) => state.weeklyTrendStartDate
);
/**
 *  Getting weekly trend tabs end target start date state to dashboard component
 */
export const selectWeeklyTrendEndDate = createSelector(
  [selectDomain],
  (state) => state.weeklyTrendEndDate
);
/**
 *  Getting hotel group list state to dashboard component
 */
export const selectHotelGroupDropdownList = createSelector(
  [selectDomain],
  (state) => state.hotelGroupsDropdownList
);
/**
 *  Getting hotel group list state to dashboard component
 */
export const selectHotelGroupsList = createSelector(
  [selectDomain],
  (state) => state.hotelGroupsList
);
/**
 *  Getting existed hotel group state to dashboard component
 */
export const selectExistedHotelGroup = createSelector(
  [selectDomain],
  (state) => state.existedHotelGroup
);
/**
 *  Getting selected hotel group name state to dashboard component
 */
export const selectHotelGroupName = createSelector([selectDomain], (state) => state.hotelGroupName);
/**
 *  Getting selected hotel group description state to dashboard component
 */
export const selectHotelDescription = createSelector([selectDomain], (state) => state.description);
/**
 *  Getting selected hotel group query state to dashboard component
 */
export const selectHotelGroupQuery = createSelector(
  [selectDomain],
  (state) => state.hotelGroupQuery
);
/**
 *  Getting selected hotel group query state to dashboard component
 */
export const selectEnableHotelGroups = createSelector(
  [selectDomain],
  (state) => state.enableHotelGroups
);
/**
 *  Getting selected summary forecast state to dashboard component
 */
export const selectSummaryForecastFilter = createSelector(
  [selectDomain],
  (state) => state.summaryForecastFilter
);
/**
 *  Getting selected landing page state to dashboard component
 */
export const selectLandingPage = createSelector([selectDomain], (state) => state.landingPage);
/**
 *  Getting selected hotel groups state to dashboard component
 */
export const selectHotelGroups = createSelector([selectDomain], (state) => state.hotelGroups);
/**
 * Getting notification list
 */
export const selectNotifications = createSelector([selectDomain], (state) => state.notifications);
/**
 * Getting merged hotel list
 */
export const selectMergedHotelList = createSelector(
  [selectDomain],
  (state) => state.mergedHotelList
);
/**
 * Getting favorite hotel list
 */
export const selectFavoriteHotelList = createSelector(
  [selectDomain],
  (state) => state.mergedHotelList
);
/**
 * Getting non favorite hotel list
 */
export const selectNonFavoriteHotelList = createSelector(
  [selectDomain],
  (state) => state.mergedHotelList
);
/**
 *  Getting assigned hotel groups state to the component
 */
export const selectAssignedHotelGroups = createSelector(
  [selectDomain],
  (state) => state.assignedHotelGroups
);
/**
 *  Getting is saved command filter loaded state to dashboard component
 */
export const selectIsSavedCommandFilterLoaded = createSelector(
  [selectDomain],
  (state) => state.IsSavedCommandFilterLoaded
);
/**
 *  Getting currency filter state to dashboard component
 */
export const selectCurrencyFilter = createSelector([selectDomain], (state) => state.currencyFilter);
/**
 *  Getting local currency state to dashboard component
 */
export const selectLocalCurrency = createSelector([selectDomain], (state) => state.localCurrency);
/**
 *  Getting local currency list state to dashboard component
 */
export const selectCurrencyList = createSelector([selectDomain], (state) => state.currencyList);
/**
 *  Getting workbookDateType to dashboard component
 */
export const selectWorkbookDateType = createSelector(
  [selectDomain],
  (state) => state.workbookDateType
);
/**
 * Getting selected hotel forecast availability state to dashboard component
 */
export const selectsHotelForecast = createSelector(
  [selectDomain],
  (state) => state.isHotelForecast
);
/**
 * Getting selected hotel forecast disabled text state to dashboard component
 */
export const selectForecastDisabledText = createSelector(
  [selectDomain],
  (state) => state.hotelForecastDisabledText
);
/**
 *  Getting predefined hotel groups state to the component
 */
export const selectPredefinedHotelGroups = createSelector(
  [selectDomain],
  (state) => state.predefinedHotelList
);
/**
 *  Getting is Hotel Deleted state to the component
 */
export const SelectIsHotelSetModified = createSelector(
  [selectDomain],
  (state) => state.isHotelSetModified
);
/**
 *  Getting is url order state to the component
 */
export const selectUrlTab = createSelector([selectDomain], (state) => state.urlTab);
/**
 *  Getting the current command url execution (id and command keys) to the component
 */
export const selectCommandRun = createSelector([selectDomain], (state) => state.commandRun);
/**
 *  Getting pms tooltip is display state to the component
 */
export const selectPmsTooltipIsDisplay = createSelector(
  [selectDomain],
  (state) => state.pmsTooltipIsDisplay
);
/**
 *  Getting app theme state to the component
 */
export const selectAppTheme = createSelector([selectDomain], (state) => state.appTheme);
/**
 *  Getting is original hotel state to the component
 */
export const selectOriginalHotelName = createSelector(
  [selectDomain],
  (state) => state.originalHotelName
);
/**
 *  Getting is original hotel list to the component
 */
export const selectOriginalHotelList = createSelector(
  [selectDomain],
  (state) => state.originalHotelList
);
/**
 *  Getting is demo mode activated state to the component
 */
export const selectIsDemoModeActivated = createSelector(
  [selectDomain],
  (state) => state.isDemoModeActivated
);
/**
 * Getting the systemInformation state into dashboard component
 */
export const selectSystemInformation = createSelector(
  [selectDomain],
  (systemState) => systemState.systemInformation
);
/**
 * Getting the app name state into dashboard component
 */
export const selectAppName = createSelector([selectDomain], (state) => state.appName);
/**
 * Getting Is Primary Hotel Set Over Time state into dashboard component
 */
export const selectIsPrimaryHotelSetOverTime = createSelector(
  [selectDomain],
  (systemState) => systemState.isPrimaryHotelSetOverTime
);

/**
 * Getting Is Primary Hotel Set Over Time Disabled state into dashboard component
 */
export const selectIsPrimaryHotelSetOverTimeDisabled = createSelector(
  [selectDomain],
  (systemState) => systemState.isPrimaryHotelSetOverTimeDisabled
);

/**
 * Getting Is Secondary Hotel Set Over Time state into dashboard component
 */
export const selectIsSecondaryHotelSetOverTime = createSelector(
  [selectDomain],
  (systemState) => systemState.isSecondaryHotelSetOverTime
);

/**
 * Getting Is Secondary Hotel Set Over Time Disabled state into dashboard component
 */
export const selectIsSecondaryHotelSetOverTimeDisabled = createSelector(
  [selectDomain],
  (systemState) => systemState.isSecondaryHotelSetOverTimeDisabled
);
/**
 * Getting Invalid Primary Hotel List state into dashboard component
 */
export const selectInvalidPrimaryHotelList = createSelector(
  [selectDomain],
  (systemState) => systemState.invalidPrimaryHotelList
);
/**
 * Getting Invalid Secondary Hotel List state into dashboard component
 */
export const selectInvalidSecondaryHotelList = createSelector(
  [selectDomain],
  (systemState) => systemState.invalidSecondaryHotelList
);
/**
 * Getting Same Store Comparison Date state into dashboard component
 */
export const selectSameStoreComparisonDate = createSelector(
  [selectDomain],
  (systemState) => systemState.sameStoreComparisonDate
);
/**
 * Get full object for a given hotel set
 */
export const selectHotelSetUpdate = () =>
  createSelector([selectDomain], (systemState) => systemState.hotelSetUpdates);

export const selectHotelSetNameUpdate = createSelector(
  [selectDomain],
  (systemState) => systemState.hotelSetUpdates
);
/**
 *Getting local booking date for dashboard component
 */
export const selectResetBookingDate = createSelector(
  [selectDomain],
  (state) => state?.resetBookingDate
);
/**
 * Get hotel natural language text
 * @returns
 */
export const selectHotelSetQuery = createSelector(
  [selectDomain],
  (systemState) => systemState.naturalLanguageText
);
/**
 * Get is leap selection state
 */
export const selectIsLeapSelection = createSelector(
  [selectDomain],
  (systemState) => systemState.isLeapSelection
);
/*
 * Get natural language query list
 * @returns
 */
export const selectNaturalLanguageQueryList = createSelector(
  [selectDomain],
  (systemState) => systemState.naturalLanguageQueryList
);
/**
 * Get llm request state
 * @returns
 */
export const selectLLMRequestState = createSelector(
  [selectDomain],
  (systemState) => systemState.initializeLLMRequest
);
/**
 * Get llm request final response state
 * @returns
 */
export const selectLLMImageResponse = createSelector(
  [selectDomain],
  (systemState) => systemState.imageExport
);
/**
 * Get llm request error notification state
 * @returns
 */
export const selectLLMErrorResponse = createSelector(
  [selectDomain],
  (systemState) => systemState.errorData
);
/**
 * Get llm data export request state
 * @returns
 */
export const selectIsDataExporting = createSelector(
  [selectDomain],
  (systemState) => systemState.isDataExporting
);

/**
 * Get llm request state
 * @returns
 */
export const selectLLMProgressSteps = createSelector(
  [selectDomain],
  (systemState) => systemState.exportProgress
);
/**
 * Get guided tour steps state
 * @returns
 */
export const selectTourSteps = createSelector(
  [selectDomain],
  (systemState) => systemState.tourSteps
);
/**
 * Get tour target state
 * @returns
 */
export const selectTourTarget = createSelector(
  [selectDomain],
  (systemState) => systemState.tourTarget
);

/**
 * Get tour run state
 * @returns
 */
export const selectTourRun = createSelector([selectDomain], (systemState) => systemState.tourRun);
/**
 * Get advanced query error message state
 * @returns
 */
export const selectAdvancedQueryErrorMessage = createSelector(
  [selectDomain],
  (systemState) => systemState.advancedQueryErrorMessage
);
/**
 * Get tour url tail state
 * @returns
 */
export const selectTourUrlTail = createSelector(
  [selectDomain],
  (systemState) => systemState.tourUrlTail
);
/**
 * Get interpreter options state
 * @returns
 */
export const selectInterpreterOptions = createSelector(
  [selectDomain],
  (systemState) => systemState.interpreterOptions
);
/**
 *Getting last full booking date for dashboard component
 */
export const selectLastFullBookingDate = createSelector(
  [selectDomain],
  (state) => state?.lastFullBookingDate
);
/**
 *Getting partial hours for dashboard component
 */
export const selectPartialHours = createSelector([selectDomain], (state) => state?.partialHours);
/**
 *Getting data last partial booking date for dashboard component
 */
export const selectLastPartialBookingDate = createSelector(
  [selectDomain],
  (state) => state?.lastPartialBookingDate
);
/**
 *Getting data max stay date for dashboard component
 */
export const selectMaxStayDate = createSelector([selectDomain], (state) => state?.maxStayDate);
/**
 *Getting open visual agent modal for dashboard component
 */
export const selectOpenVisualAgentModal = createSelector(
  [selectDomain],
  (state) => state?.openVisualAgentModal
);
/**
 *Getting visual analysis for dashboard component
 */
export const selectVisualAnalysis = createSelector(
  [selectDomain],
  (state) => state?.visualAnalysis
);
/**
 *Getting whether the VI drawer overlay is greyed out for a historical VI result
 */
export const selectIsHistoricalViOverlayActive = createSelector(
  [selectDomain],
  (state) => state?.isHistoricalViOverlayActive
);
/**
 * Getting whether the last user VI response list request failed
 */
export const selectViResponseListError = createSelector(
  [selectDomain],
  (state) => state?.viResponseListError
);
/**
 * Getting whether the user VI response list request is in progress
 */
export const selectViResponseListLoading = createSelector(
  [selectDomain],
  (state) => state?.viResponseListLoading
);
/**
 *Getting is first time interpreter agent used state for dashboard component
 */
export const selectIsFirstTimeInterpreterAgentUsed = createSelector(
  [selectDomain],
  (state) => state?.isFirstTimeInterpreterAgentUsed
);
/**
 *Getting is first time interpreter agent used state for dashboard component
 */
export const selectInterpreterAgentClicked = createSelector(
  [selectDomain],
  (state) => state?.interpreterAgentClicked
);
/**
 *Getting max forecast date for dashboard component
 */
export const selectMaxForecastDate = createSelector(
  [selectDomain],
  (state) => state?.maxForecastDate
);
/**
 * Getting share url tab list component
 */
export const selectShareUrlTabList = createSelector(
  [selectDomain],
  (state) => state.shareUrlTabList
);
/**
 * Getting tab list to the component
 */
export const selectTabList = createSelector([selectDomain], (state) => state.tabList);
/**
 * Getting the key of the selected tab, e.g. 'PACE'
 */
export const selectTabKey = createSelector([selectTabValue, selectTabList], (tabValue, tabList) =>
  Object.keys(tabList ?? {}).find((key) => tabList[key] === tabValue)
);
/**
 * Getting tab list to the component
 */
export const selectEncodedTabList = createSelector([selectDomain], (state) => state.encodedTabList);
